export type WearCategory = 'active-wear' | 'yoga-wear';

export interface CataloguePresetPose {
  poseName: string;
  cloudStorageUrl: string;
  prompt?: string;
}

export interface CatalogueGeneratePresetItem {
  modelName: string;
  poses: CataloguePresetPose[];
  modelId: string;
  profilePic: string;
  faceImageUrl?: string;
  active: boolean;
  gender: string;
  timestamp: string;
  type: string;
}

export interface WearCataloguePosePayload {
  poseName: string;
  prompt: string;
  previewImageUrl: string;
}

export interface WearCatalogueGeneratePayload {
  faceImageUrl: string;
  clothesImageUrl: string;
  poses: WearCataloguePosePayload[];
  modelId?: string;
  category?: WearCategory;
  mode?: string;
  enable4k?: boolean;
  userId?: string;
  suffixPrompt?: string;
}

export const normalizeCataloguePreset = (model: Record<string, unknown>): CatalogueGeneratePresetItem => ({
  modelId: String(model.modelId ?? ''),
  modelName: String(model.modelName ?? ''),
  gender: String(model.gender ?? ''),
  profilePic: String(model.profilePic ?? model.faceImageUrl ?? ''),
  faceImageUrl: String(model.faceImageUrl ?? model.profilePic ?? ''),
  active: Boolean(model.active ?? true),
  timestamp: String(model.timestamp ?? ''),
  type: String(model.type ?? ''),
  poses: Array.isArray(model.poses)
    ? model.poses.map((pose: Record<string, unknown>) => ({
      poseName: String(pose.poseName ?? ''),
      cloudStorageUrl: String(pose.previewImageUrl ?? pose.cloudStorageUrl ?? ''),
      prompt: String(pose.prompt ?? pose.posePrompt ?? ''),
    }))
    : [],
});

export const flattenWearInputImages = (input: unknown): string[] => {
  if (Array.isArray(input)) {
    return input.filter((url): url is string => typeof url === 'string');
  }

  if (input && typeof input === 'object') {
    return Object.values(input as Record<string, unknown>).flatMap((pair) => (
      Array.isArray(pair) ? pair.filter((url): url is string => typeof url === 'string') : []
    ));
  }

  return [];
};

export const buildWearPosePayload = (
  pose: CataloguePresetPose,
  suffixPrompts: Record<string, string>,
): WearCataloguePosePayload => {
  const extraPrompt = suffixPrompts[pose.poseName]?.trim();
  const basePrompt = pose.prompt?.trim() || pose.poseName;

  return {
    poseName: pose.poseName,
    prompt: extraPrompt ? `${basePrompt} ${extraPrompt}` : basePrompt,
    previewImageUrl: pose.cloudStorageUrl,
  };
};

export const groupSelectedPosesByGarment = (
  selectedPoseNames: string[],
  selectedPosesMap: Record<string, CataloguePresetPose>,
  getGarmentUrlForCategory: (category: string) => string,
): Map<string, CataloguePresetPose[]> => {
  const garmentGroups = new Map<string, CataloguePresetPose[]>();

  selectedPoseNames.forEach((category) => {
    const pose = selectedPosesMap[category];
    const garmentUrl = getGarmentUrlForCategory(category);
    if (!pose || !garmentUrl) return;

    const existing = garmentGroups.get(garmentUrl) ?? [];
    existing.push(pose);
    garmentGroups.set(garmentUrl, existing);
  });

  return garmentGroups;
};

export const buildWearCataloguePayload = (
  selectedModel: CatalogueGeneratePresetItem,
  poses: CataloguePresetPose[],
  clothesImageUrl: string,
  category: WearCategory,
  mode: string,
  enable4k: boolean,
  userId: string,
  suffixPrompts: Record<string, string>,
): WearCatalogueGeneratePayload => ({
  faceImageUrl: selectedModel.faceImageUrl || selectedModel.profilePic,
  clothesImageUrl,
  poses: poses.map((pose) => buildWearPosePayload(pose, suffixPrompts)),
  modelId: selectedModel.modelId,
  category,
  mode: mode.toLowerCase(),
  enable4k,
  userId,
});
